import { format, parseISO, addMinutes } from "date-fns";
import { toast } from "react-toastify";
import Label from "src/components/Label";
import { formatISO } from "date-fns";
import {
  createBOSetting,
  getSingleDoc,
  getOrgControl,
  createOrgLimit,
} from "./FinmoNetworkUtils";
import { currency_decimal, decimal_format_list } from "./globalStaticData";
import {
  finmo_country_to_currency,
  finmo_platform_supported_currency_list,
} from "src/utils/countryData";
import numeral from "numeral";
import { useQuery } from "src/hooks/querys";
import { TOKENIZATION_ANCHOR } from "src/config";
import crypto from "crypto-browserify";
import Papa from "papaparse";
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from "postcode-validator";
import {
  ORG_LIMIT_DURATION,
  ORG_LIMIT_SCOPE,
  ORG_LIMIT_TYPE,
} from "src/content/dashboards/LimitGlance/staticData";

export const tokenizeData = (data) => {
  const token = Buffer.from(TOKENIZATION_ANCHOR, "base64").toString("utf8");
  const buffer = Buffer.from(data, "utf8");
  const encrypted = crypto.publicEncrypt(token, buffer);
  return encrypted.toString("base64");
};

export const reduxErrorMessage = (error) =>
  error.response && error.response.data.message
    ? error.response.data.message
    : error.message;

export const apiErrorMessage = (error) => {
  if (Array.isArray(error?.response && error?.response?.data?.error?.message)) {
    const message = error?.response && error?.response?.data?.error?.message;
    return message?.join(" , ");
  } else if (error?.response && error?.response?.data?.message) {
    return error?.response?.data?.message;
  } else {
    return error?.response && error?.response?.data?.error
      ? error?.response?.data?.error?.message
      : error?.message;
  }
};

export const limitStr = (str, len, endsWith = "") => {
  if (str.length < len) return str;
  return str.substring(0, len) + endsWith;
};

export const deleteObjProperty = (obj, keys) => {
  const cloneObj = { ...obj };
  keys.map((key) => delete cloneObj[key]);
  return cloneObj;
};

export const getISOformatString = (value) => {
  return `${formatISO(new Date(value), {
    representation: "complete",
  })
    .split(":")
    .slice(0, 2)
    .join(":")}:00Z`;
};
export const printObjProperty = (obj, keys) => {
  const clone = { ...obj };
  Object.keys(clone).forEach((key) => keys.includes(key) || delete clone[key]);
  return clone;
};

export const objToKeyPairArr = (obj) => Object.entries(obj);

export const rmUnderScore = (str) => str.replaceAll("_", " ");

export const isEmptyObj = (obj) =>
  Object.entries(obj).length === 0 && obj.constructor === Object;

export const isObject = (obj) =>
  typeof obj === "object" && obj !== null && !Array.isArray(obj);

export const toastNotification = (type, message, time, position) => {
  toast[type || "error"](message || "", {
    position: position || "bottom-center",
    autoClose: time || 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

export const matchValue = (val1, val2) => {
  let value1;
  let value2;
  if (val1 && val2) {
    value1 = val1.toLowerCase();
    value2 = val2.toLowerCase();
    if (value1 == value2) {
      return <Label color={"success"}>Matched</Label>;
    }
  }
  return <Label color={"error"}>Unmatched</Label>;
};

export const isObjNull = (obj) =>
  Object.values(obj).every((doc) => {
    if (doc === null) {
      return true;
    }
    return false;
  });

export const pricingMessage =
  "All pricing shown below excludes local taxes and GST";

export const unixFun = (v) => {
  const unix =
    v !== null ? (isNaN(v) ? null : Math.floor(v.getTime() / 1000)) : null;
  return unix;
};

export const pricingModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  height: "auto",
  bgcolor: "#fff",
  borderRadius: "8px",
  border: "0px solid #000",
  boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
  p: 4,
};

export const downloadDoc = async (org_id, doc_id) => {
  try {
    const { success, data } = await getSingleDoc(org_id, doc_id);
    if (success) {
      window.open(data.download_url, "_blank");
    }
  } catch (err) {
    toastNotification("error", apiErrorMessage(err), 2000);
  }
};

export const setCustomColumnSettings = (columns, setTableCustomColumnData) => {
  setTableCustomColumnData((prevState) => {
    return prevState.map((prevStateObj) => {
      if (columns?.includes(prevStateObj.columnName)) {
        return { ...prevStateObj, isVisible: true };
      } else {
        return { ...prevStateObj, isVisible: false };
      }
    });
  });
};

export const createCustomColumnSettings = async (
  columns,
  column_setting_key,
  user
) => {
  let data = [];
  columns.forEach((item) => {
    if (item.isVisible) {
      data.push(item.columnName);
    }
  });
  try {
    await createBOSetting(
      {
        environment: "all",
        key: column_setting_key,
        value: data,
      },
      user.bo_user_id
    );
  } catch (err) {}
};

export const convertDateToMonthYear = (date, convert = true) => {
  // MONTH YEAR (JUL 2023)
  if (new Date(date).getTime() > 0) {
    return format(
      new Date(new Date(date).getTime() - (convert ? 86400000 : 0)),
      "MMM yyyy"
    );
  }
};

export const handleAmountChange = (val, curr) => {
  const decimals =
    currency_decimal[curr] === 0
      ? 0
      : currency_decimal[curr]
      ? currency_decimal[curr]
      : 2;
  const arr = val.toString().split(".");
  if (arr.length === 1) return val;
  const int = arr[0],
    max = arr[1].length,
    dec = arr[1].substr(0, decimals > max ? max : decimals);
  const amount = decimals === 0 ? int : [int, ".", dec].join("");
  return amount;
};

export const getOrgEnabledCountryCurrency = async (org_id, env) => {
  try {
    let enabled_country_currency_map = {};
    let enabled_country = [];
    let enabled_currency = [];
    const filters = [
      { filter_key: "environment", filter_value: env },
      { filter_key: "limit", filter_value: 1000 },
    ];

    const data = await Promise.all([
      getOrgControl(
        [...filters, { filter_key: "key_type", filter_value: "COUNTRY" }],

        org_id,
        env
      ),
      getOrgControl(
        [...filters, { filter_key: "key_type", filter_value: "CURRENCY" }],
        org_id,
        env
      ),
    ]);

    const country_data = data?.[0]?.data;
    const currency_data = data?.[1]?.data;

    country_data.forEach((country) => {
      if (
        country?.value !== null &&
        country?.value?.is_enabled &&
        Object.keys(finmo_country_to_currency).includes(country?.key)
      ) {
        let enabled_currency_for_country = [];
        currency_data.forEach((currency) => {
          if (
            currency?.value !== null &&
            currency?.value?.is_enabled &&
            finmo_country_to_currency[country?.key].includes(currency?.key)
          ) {
            enabled_country.push(country?.key);
            enabled_currency.push(currency?.key);
            enabled_currency_for_country.push(currency?.key);
          }
        });
        if (enabled_currency_for_country.length > 0) {
          enabled_country_currency_map[
            country?.key
          ] = enabled_currency_for_country;
        }
      }
    });
    return {
      enabled_country,
      enabled_currency,
      enabled_country_currency_map,
      env,
    };
  } catch (error) {
    toastNotification("error", apiErrorMessage(error), 3000);
  }
};

export const getOrgSettlementEnabledCurrency = async (org_id, env) => {
  try {
    let settlement_enabled_currency_list = [];
    let settlement_payout_enabled_currency_list = [];
    const filters = [
      { filter_key: "environment", filter_value: env },
      { filter_key: "limit", filter_value: 1000 },
    ];

    const data = await Promise.all([
      getOrgControl(
        [...filters, { filter_key: "key_type", filter_value: "SETTLEMENT" }],

        org_id,
        env
      ),
      getOrgControl(
        [
          ...filters,
          { filter_key: "key_type", filter_value: "SETTLEMENT_PAYOUT" },
        ],
        org_id,
        env
      ),
    ]);

    const settlement_data = data?.[0]?.data;
    const settlement_payout_data = data?.[1]?.data;

    settlement_data.forEach((currency) => {
      if (
        currency?.value !== null &&
        currency?.value?.is_enabled &&
        finmo_platform_supported_currency_list.includes(currency?.key)
      ) {
        settlement_enabled_currency_list.push(currency?.key);
      }
    });

    settlement_payout_data.forEach((currency) => {
      if (
        currency?.value !== null &&
        currency?.value?.is_enabled &&
        finmo_platform_supported_currency_list.includes(currency?.key)
      ) {
        settlement_payout_enabled_currency_list.push(currency?.key);
      }
    });

    return {
      settlement_enabled_currency_list,
      settlement_payout_enabled_currency_list,
    };
  } catch (error) {
    toastNotification("error", apiErrorMessage(error), 3000);
  }
};

export const getOrgCustomerWalletPayoutEnabledCurrency = async (
  org_id,
  env
) => {
  try {
    let customer_payout_enabled_currency_list = [];
    const filters = [
      { filter_key: "environment", filter_value: env },
      { filter_key: "limit", filter_value: 1000 },
    ];

    const data = await Promise.all([
      getOrgControl(
        [
          ...filters,
          { filter_key: "key_type", filter_value: "CUSTOMER_WALLET_PAYOUT" },
        ],

        org_id,
        env
      ),
    ]);

    const settlement_data = data?.[0]?.data;

    settlement_data.forEach((currency) => {
      if (
        currency?.value !== null &&
        currency?.value?.is_enabled &&
        finmo_platform_supported_currency_list.includes(currency?.key)
      ) {
        customer_payout_enabled_currency_list.push(currency?.key);
      }
    });

    return {
      customer_payout_enabled_currency_list,
      env,
    };
  } catch (error) {
    toastNotification("error", apiErrorMessage(error), 3000);
  }
};

export const getEnabledPlugins = async (org_id, env) => {
  try {
    let plugins_list = [];

    const { data } = await getOrgControl(
      [
        { filter_key: "environment", filter_value: env },
        { filter_key: "limit", filter_value: 1000 },
        { filter_key: "key_type", filter_value: "PLUGIN" },
      ],
      org_id,
      env
    );

    data.forEach((plugin) => {
      plugins_list.push({ key: plugin?.key, value: plugin?.value });
    });

    return {
      plugins_list,
      env,
    };
  } catch (error) {
    toastNotification("error", apiErrorMessage(error), 3000);
  }
};

export const verifyDocSizeType = (e, fileSize = 2000000) => {
  if (
    e.target.files[0] &&
    (e.target.files[0].type === "application/pdf" ||
      e.target.files[0].type === "image/jpeg" ||
      e.target.files[0].type === "image/png" ||
      e.target.files[0].type === "image/jpg")
  ) {
    if (Number(e.target.files[0].size) >= fileSize) {
      toastNotification(
        "error",
        `File size should not be more than ${
          fileSize === 2000000 ? "2MB" : `${fileSize / 1000000}MB`
        }`,
        2000
      );
      return false;
    }
  } else if (e.target.value !== "") {
    toastNotification(
      "error",
      "DOC format should be in PDF/JPEG/PNG/JPG",
      2000
    );
    return false;
  }

  return true;
};
export const toDecimal = (num, curr) => {
  const decimals =
    currency_decimal[curr] === 0
      ? 0
      : currency_decimal[curr]
      ? currency_decimal[curr]
      : 2;
  return parseFloat(num?.toFixed(decimals));
};
export const toCurrencyBasedDecimal = (num, curr) => {
  const format = decimal_format_list[currency_decimal[curr]];
  return numeral(toDecimal(num, curr)).format(format);
};

export const getCurrencyFlagPath = (currency) => {
  try {
    return require(`../assests/images/currencyflags/${currency.toLowerCase()}.png`)
      .default;
  } catch (err) {
    return null;
  }
};
export const getConnectorIconPath = (connector_name) => {
  try {
    return require(`../assests/images/connectorIcons/${connector_name}.svg`)
      .default;
  } catch (err) {
    return require(`../assests/images/connectorIcons/ocbc.svg`).default;
  }
};

export const GetAllQueryString = (remove_keys = []) => {
  const query = useQuery();
  let queryString = [];
  query.forEach((value, key) => {
    if (!remove_keys.includes(key)) {
      queryString.push({ filter_key: key, filter_value: value });
    }
  });
  return queryString;
};

export const formatTimeFilterData = (start_time, end_time) => {
  const dateFilter = [];

  if (start_time) {
    dateFilter.push({
      filter_key: "start_time",
      filter_value: Math.floor(start_time.getTime() / 1000),
    });
  }
  if (end_time) {
    dateFilter.push({
      filter_key: "end_time",
      filter_value: Math.floor(end_time.getTime() / 1000),
    });
  }

  return dateFilter;
};

export const convertArrayToObject = (array) => {
  const obj = {};
  array.forEach((item) => {
    obj[item.field_key] = item.field_value;
  });
  return obj;
};

export const provideDomain = () => {
  return window.location.hostname.includes("qafinmo") ? "qafinmo" : "finmo";
};

export const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

export const toSnakeCase = (str) => {
  return str.trim().replace(/\s+/g, "_").replace(/-/g, "_").toLowerCase();
};

export const snakeToUpperFirstCase = (str) => {
  return str
    .trim()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const isDateLike = (value) => {
  if (!value && value !== 0) return false;
  const date = value instanceof Date ? value : new Date(value);
  return !isNaN(date.getTime());
};

export const csvToJsonHelper = (file_data) => {
  const parsed_data = Papa.parse(file_data, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true,
  });

  const headers = parsed_data.meta.fields.map((field) => toSnakeCase(field));

  const json_data = parsed_data.data.map((row) => {
    return headers.reduce((acc, header, index) => {
      acc[header] = row[parsed_data.meta.fields[index]];
      return acc;
    }, {});
  });

  return json_data;
};

export const getTieredPricingReqObj = ({
  max_fees,
  min_fees,
  st_fees,
  sf_currency,
  per_fees,
  tier_name,
  type,
  tier_start,
  tier_end,
  is_default,
}) => ({
  max_fees: max_fees ? +max_fees : null,
  min_fees: min_fees ? +min_fees : null,
  st_fees: st_fees ? +st_fees : null,
  sf_currency: sf_currency ? sf_currency : null,
  per_fees: per_fees ? +per_fees : null,
  tier_name: tier_name ? tier_name : null,
  type: type ? type : null,
  tier_start: tier_start ? +tier_start : null,
  tier_end: tier_end ? +tier_end : null,
  is_default: is_default ? is_default : false,
});

export function capitalizeWords(str) {
  if (!str) return "";
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
}
// Returns true if the org is in offboarding (status is set and not PENDING_APPROVAL or RELEASED)
export const isOrgInOffboarding = (status) => {
  return !!status && status !== "PENDING_APPROVAL" && status !== "RELEASED";
};

export const findNameByUserId = (bo_user_id, bo_users = []) => {
  if (!bo_user_id) return "-";

  const user = Array.isArray(bo_users)
    ? bo_users.find((u) => u.bo_user_id === bo_user_id)
    : null;

  if (user && (user.first_name || user.last_name)) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }

  return bo_user_id || "-";
};

export const zipCodeValidator = (zip_code, country, isRequired) => {
  const hasZipCode = zip_code && zip_code.trim().length > 0;
  const is_zip_code_valid = postcodeValidatorExistsForCountry(country);
  if (isRequired && !hasZipCode) {
    return false;
  }
  if (!is_zip_code_valid || !hasZipCode) {
    return true;
  }
  return postcodeValidator(zip_code, country);
};

export const getReqShareholderDirectorObj = (values) => {
  const final_obj = {};
  Object.keys(values)
    .filter((key) => !["identification_doc", "address_proof_doc"].includes(key))
    .map((key) => {
      final_obj[key] = values[key] === "" ? null : values[key];
    });
  return final_obj;
};

export const convertUTCLabelsToLocal = (labels, startDateISO) => {
  if (!labels || labels.length === 0) return labels;

  const first = (labels[0] || "").trim();

  // Format: "YYYY-MM-DD HH:MM" — full UTC datetime, spans multiple days
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(first)) {
    return labels.map((label) => {
      const localDate = parseISO(label.trim().replace(" ", "T") + ":00Z");
      return format(localDate, "dd MMM HH:mm");
    });
  }

  // Format: "HH:MM" — plain UTC time on a single day (1d/minute granularity)
  if (/^\d{1,2}:\d{2}$/.test(first)) {
    if (!startDateISO) return labels;
    let baseDate = parseISO(startDateISO + "T00:00:00Z");
    let prevTotalMins = -1;

    return labels.map((label) => {
      const [h, m] = label.trim().split(":").map(Number);
      const totalMins = h * 60 + m;
      // Detect midnight rollover (time went backwards → new UTC day)
      if (prevTotalMins !== -1 && totalMins < prevTotalMins) {
        baseDate = addMinutes(baseDate, 24 * 60);
      }
      prevTotalMins = totalMins;
      return format(addMinutes(baseDate, totalMins), "HH:mm");
    });
  }

  // Format: "DD MMM YYYY" — daily bucket label, return as-is
  return labels;
};

export const formatDateToYMD = (d) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const capitalizeFirst = (str) => {
  return str ? str.slice(0, 1).toUpperCase() + str.slice(1) : "";
};

export const createOrgLimitForStables = async (org_id, val, key_type) => {
  try {
    await createOrgLimit({
      org_id,
      scope: ORG_LIMIT_SCOPE.ORG.value,
      key_type: key_type,
      type: ORG_LIMIT_TYPE.VOLUME.value,
      duration: ORG_LIMIT_DURATION.INFINITE.value,
      threshold: val,
    });
  } catch (err) {
    toastNotification("error", apiErrorMessage(err), 2000);
  }
};

export const parseTruliooUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;
    return {
      record_id: params.get("transactionRecordId") || null,
      provider_reference_id: params.get("transactionId") || null,
    };
  } catch {
    return { record_id: null, provider_reference_id: null };
  }
};

export const generateTruliooLink = (record_id, provider_reference_id) => {
  if (!record_id) return null;

  if (provider_reference_id) {
    // business_verify type link
    return `https://portal.trulioo.com/kyb/verification?transactionRecordId=${record_id}&transactionId=${provider_reference_id}`;
  }
  // business_search type link
  return `https://portal.trulioo.com/kyb/verification?transactionRecordId=${record_id}&businessSearch=true`;
};

// Format number according to specified format (international or indian)
export const formatNumber = (value, format) => {
  if (value === null || value === undefined || value === "" || value === "NA") {
    return value;
  }

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  if (format === "none") {
    return num.toFixed(2);
  }

  const fixed_num = num.toFixed(2);
  const [integerPart, decimalPart = "00"] = fixed_num.split(".");
  const isNegative = integerPart.startsWith("-");
  const absInteger = isNegative ? integerPart.slice(1) : integerPart;

  let formattedInteger;
  if (format === "international") {
    // International format: 1,234,567.89
    formattedInteger = absInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } else if (format === "indian") {
    // Indian format: 12,34,567.89
    const lastThree = absInteger.slice(-3);
    const otherNumbers = absInteger.slice(0, -3);
    if (otherNumbers !== "") {
      formattedInteger =
        otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
    } else {
      formattedInteger = lastThree;
    }
  }

  return (isNegative ? "-" : "") + formattedInteger + "." + decimalPart;
};
